<template>
  <div
    v-if="role != 'stylist' "
    v-click-outside="onClickOutside"
    class="profile-selector"
    :style="{'margin-top': (!responsive.lg) ? '14px': ''}"
  >
    <!-- Tablet screen label -->
    <div
      class="profile-selector__label"
    >
      {{ assignToLabel }}
    </div>
    <div
      v-if="!hasPermission"
    >
      <base-text
        trim
        max-width="230"
        style="font-size: 14px;font-family: 'SilkaLight';"
      >
        <template v-if="innerSelected">
          {{ innerSelected.user.first_name }} {{ innerSelected.user.last_name }}
        </template>
        <template v-else-if="assigned">
          {{ loading ? "" : (assigned.user ? associateName(assigned) : "Unassign") }}
        </template>
      </base-text>
    </div>
    <div
      v-else
      class="profile-selector__button px-3 selector rounded"
      :style="customStyle"
    >
      <div
        ref="profileList"
        class="d-flex justify-content-center align-items-center py-2 toggle-selector"
        @click="toggleSelector"
      >
        <base-text
          trim
          max-width="230"
        >
          <template v-if="innerSelected">
            {{ innerSelected.user.first_name }} {{ innerSelected.user.last_name }}
          </template>
          <template v-else-if="assigned">
            {{ loading ? "" : (assigned.user ? associateName(assigned) : "Unassign") }}
          </template>
        </base-text>
        <font-awesome-icon
          v-if="loading && !innerSelected"
          class="ml-auto"
          icon="spinner"
          spin
        />
        <base-icon
          v-else
          class="ml-auto"
          icon="arrow-down"
        />
      </div>
    </div>
    <div
      ref="popper"
      class="profile-selector__popover"
    >
      <div
        class="profile-selector__popover-inner justify-content-center pb-3 px-3 rounded"
        :style="customStyle"
      >
        <div
          ref="profileList"
          class="d-flex justify-content-center align-items-center py-2 toggle-selector"
          @click="selector=!selector"
        >
          <base-text
            trim
            max-width="230"
          >
            {{ loading ? "" : (assigned !== null ? associateName(assigned) : "Unassign") }}
          </base-text>
          <base-icon
            class="ml-auto"
            icon="arrow-up"
          />
        </div>
        <div
          v-if="hasPermission"
          class="pt-2 pb-1"
        >
          <base-button
            borderless
            block
            class="unassign-button py-1"
            @click="removeAssociate"
          >
            Unassign
          </base-button>
        </div>
        <div
          class="py-2"
        >
          <base-search
            ref="search"
            :value="search"
            :show-icon="false"
            @input="$emit('on-search', $event)"
          />
        </div>
        <div
          class="profile-selector__content"
          @scroll="onScroll"
        >
          <div
            v-for="(profile, index) in profiles"
            :key="`profileSelector_${index}`"
          >
            <div
              class="d-flex justify-content-between py-1"
              :style="hasPermission ? { 'cursor': 'pointer' } : disableCustomer"
              @click="assignAssociate(profile)"
            >
              <base-text
                trim
                max-width="180"
                size="s"
                :bold="assigned && profile.pk === assigned.pk"
              >
                {{ associateName(profile) }}
              </base-text>
              <base-icon
                v-if="assigned && profile.pk === assigned.pk"
                icon="colored-check"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { createPopper } from '@popperjs/core';
import responsive from '@/components/mixins/responsive';

export default {
  name: 'ProfileSelector',
  directives: {
    'click-outside': {
      bind(el, binding, vnode) {
        el.clickOutsideEvent = (event) => {
          if (!(el === event.target || el.contains(event.target))) {
            vnode.context[binding.expression](event);
          }
        };
        document.body.addEventListener('click', el.clickOutsideEvent);
      },
      unbind(el) {
        document.body.removeEventListener('click', el.clickOutsideEvent);
      },
    },
  },
  mixins: [responsive],
  props: {
    profiles: {
      type: Array,
      default: () => [],
    },
    assigned: {
      type: Object,
      default: () => {},
    },
    search: {
      type: String,
      default: '',
    },
    showUnassign: {
      type: Boolean,
      default: true,
    },
    customWidth: {
      type: String,
      default: '',
    },
    assignToLabel: {
      type: String,
      default: 'ASSIGNMENT',
    },
    loading: {
      type: Boolean,
      default: false,
    },
    count: {
      type: Number,
      default: 0,
    },
    hasPermission: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selector: false,
      instance: null,
      element: '',
      popper: '',
      innerSelected: null,
      role: '',
    };
  },
  computed: {
    disableCustomer() {
      let disableCustomer = '';
      disableCustomer += 'color: #666666;';
      disableCustomer += 'pointer-events: none;';
      return disableCustomer;
    },
    customStyle() {
      let customStyle = '';
      if (this.customWidth) {
        customStyle += `width: ${this.customWidth}px; `;
      }
      return customStyle;
    },
  },
  watch: {
    selector(newVal) {
      if (newVal) {
        this.popper.setAttribute('data-show', '');
        this.createPoppover();
      } else {
        this.popper.removeAttribute('data-show');
        this.destroyPoppover();
      }
    },
  },
  created() {
    this.hasPermission = this.usePermission('assign_and_unassign_client_privilege') && this.$store.state.account.isAdminMode;
    this.role = this.$store.state.account.user.profile.role;
  },
  mounted() {
    this.$nextTick().then(() => {
      this.element = this.$refs.profileList;
      this.popper = this.$refs.popper;
    });
  },

  methods: {
    onClickOutside() {
      if (this.selector) {
        this.toggleSelector();
      }
    },
    toggleSelector() {
      this.selector = !this.selector;

      this.$nextTick().then(() => {
        if (this.selector) {
          this.$refs.search.focus();
        }
      });
    },
    associateName(associate) {
      if (associate.user !== undefined) {
        return `${associate.user.first_name} ${associate.user.last_name}`;
      }
      return '';
    },
    assignAssociate(profile) {
      this.toggleSelector();
      this.innerSelected = profile;
      this.$emit('assign', profile);
    },
    removeAssociate() {
      this.toggleSelector();
      this.innerSelected = null;
      this.$emit('unassign');
    },
    createPoppover() {
      this.instance = createPopper(this.element, this.popper, {
        placement: 'bottom',
        modifiers: [{
          name: 'offset',
          options: {
            offset: () => {
              console.log('No offset fixes firefox');
            },
          },
        }],
      });
    },
    destroyPoppover() {
      if (this.instance) {
        this.instance.destroy();
        this.instance = null;
      }
    },
    onScroll({ target: { scrollTop, clientHeight, scrollHeight } }) {
      if (scrollTop + clientHeight >= scrollHeight) {
        // Bottom reached
        if (this.profiles.length <= this.count) {
          // Still elements to retrieve
          this.$emit('reached-bottom');
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.selector {
    border: solid 1px #33333350;
    background-color: #FFFFFF;
    width: 100%;
    font-size: 13px !important;
}

.profile-selector {
    position: relative;
    &__label {
        font-family: $font-family-sans-serif-light;
        font-style: normal;
        font-weight: normal;
        font-size: 0.75rem;
        line-height: 14px;
        letter-spacing: 0.105px;
        text-transform: uppercase;
        color: #AAAAAA;
        height: 20px;
        display: flex;
        align-items: center;
    }
    &__button {
      cursor: pointer;
      border: 1px solid $gray-light-4;
    }
    &__popover {
        z-index: 99999;
        opacity: 0;
        pointer-events: none;
        position: fixed;
        right: 0;
        &[data-show] {
            opacity: 1 !important;
            pointer-events: inherit;
        }
    }
    &__content{
      overflow-y: auto;
      max-height: 100%;
    }

    &__popover-inner {
      background-color: #FFFFFF;
      border: solid 1px #ccc;
      width: 100%;
      display: flex;
      flex-direction: column;
      max-height: 280px;
      font-size: 13px;
    }
}

.unassign-button {
    background-color: #F2F1EE;
}

::v-deep .base-search__input {
    background-color: #FFFFFF;
    border: solid 1px #33333350;
    border-radius: 3px;
    color: #333333;
    width: 100%;
    // min-width: 204px;
}

::v-deep .base-search {
  min-width: unset;
  margin-right:0;
}

.toggle-selector{
    min-height: 35px; // Safari fix
}
</style>
